import React, { useState, useRef, useEffect } from 'react';
import { App, Modal, Button, Space, Alert, Spin, Upload, Card, Typography } from 'antd';
import {
  CameraOutlined,
  CloseOutlined,
  UploadOutlined,
  ScanOutlined,
} from '@ant-design/icons';
import { BrowserMultiFormatReader } from '@zxing/browser';
import type { UploadFile } from 'antd';
import { useResponsive } from '../../hooks/useResponsive';

const { Text, Title } = Typography;

type ScanMethod = 'select' | 'camera' | 'upload' | 'manual';

/**
 * Textos del modal. Estaban hardcodeados a "Constancia Fiscal SAT" porque el único
 * consumidor era Facturación; ahora también lo usa la autocaptura de gastos, donde
 * hablar de constancias fiscales sería sencillamente falso.
 */
export interface QRScannerCopy {
  selectTitle: string;
  selectDescription: string;
  cameraHint: string;
  uploadTitle: string;
  uploadDescription: string;
}

const FACTURACION_COPY: QRScannerCopy = {
  selectTitle: 'Escanear QR de Constancia Fiscal SAT',
  selectDescription:
    'Seleccione cómo desea proporcionar el código QR de su constancia de situación fiscal.',
  cameraHint: 'Apunte la cámara hacia el código QR de su constancia fiscal SAT.',
  uploadTitle: 'Subir imagen del código QR',
  uploadDescription:
    'Seleccione una imagen que contenga el código QR de su constancia de situación fiscal del SAT.',
};

interface QRScannerModalProps {
  visible: boolean;
  onClose: () => void;
  /** Modo Facturación: entrega el TEXTO del QR. */
  onQRDetected?: (qrContent: string) => void;
  onManualEntry?: () => void;
  copy?: Partial<QRScannerCopy>;

  /**
   * Modo CAPTURA (autocaptura de gastos): entrega **únicamente el frame** como JPEG.
   *
   * El navegador sí lee el texto del QR —`@zxing` está aquí y es lo que dispara la foto—
   * pero ese texto **no sale de este componente**. El servidor lo relee de los bytes que
   * recibe, y esa relectura es la única autoritativa.
   *
   * No es purismo. Si el texto viajara en el payload, bastaría con que alguien lo
   * "aprovechara para no re-decodificar" para reabrir el agujero que cerró la rev. 3 del
   * spec: subir la factura A y escanear el QR de la B, con el QR ganando en silencio. El
   * archivo y sus datos tienen que ser el mismo documento por construcción, no por
   * promesa.
   */
  onFrameCaptured?: (frame: Blob) => void;
}

const QRScannerModal: React.FC<QRScannerModalProps> = ({
  visible,
  onClose,
  onQRDetected,
  onManualEntry,
  copy,
  onFrameCaptured,
}) => {
  const { modal } = App.useApp();
  const texts: QRScannerCopy = { ...FACTURACION_COPY, ...copy };
  const captureMode = typeof onFrameCaptured === 'function';
  const { isMobile } = useResponsive();
  const [method, setMethod] = useState<ScanMethod>('select');
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [attemptCount, setAttemptCount] = useState(0);
  const [fileList, setFileList] = useState<UploadFile[]>([]);

  const videoRef = useRef<HTMLVideoElement>(null);
  const codeReaderRef = useRef<BrowserMultiFormatReader | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const maxAttempts = 3;
  const isMobileDevice = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  // Cleanup on unmount or close
  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, []);

  // Reset when modal closes
  useEffect(() => {
    if (!visible) {
      stopCamera();
      setMethod('select');
      setError(null);
      setAttemptCount(0);
      setFileList([]);
    }
  }, [visible]);

  const stopCamera = () => {
    if (codeReaderRef.current) {
      // BrowserMultiFormatReader doesn't have a reset method, just clear the reference
      codeReaderRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setScanning(false);
  };

  const startCameraScanning = async () => {
    setScanning(true);
    setError(null);

    try {
      // Check HTTPS requirement
      if (window.location.protocol !== 'https:' && window.location.hostname !== 'localhost') {
        throw new Error('El acceso a la cámara requiere HTTPS');
      }

      const codeReader = new BrowserMultiFormatReader();
      codeReaderRef.current = codeReader;

      // Request camera permission with mobile-optimized settings
      const constraints: MediaStreamConstraints = {
        video: {
          facingMode: isMobileDevice ? 'environment' : 'user',
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;

        // Start decoding
        await codeReader.decodeFromVideoElement(
          videoRef.current,
          (result: any, error: any) => {
            if (result) {
              const qrContent = result.getText();
              handleQRDetected(qrContent);
            }
            if (error && error.name !== 'NotFoundException') {
              console.error('❌ QR decode error:', error);
            }
          }
        );
      }
    } catch (err: unknown) {
      console.error('Camera error:', err);
      const errorMsg = err instanceof Error ? err.message : 'Error al acceder a la cámara';

      if (errorMsg.includes('Permission denied') || errorMsg.includes('NotAllowedError')) {
        setError('Permiso de cámara denegado. Por favor, permita el acceso a la cámara en su navegador.');
      } else if (errorMsg.includes('NotFoundError')) {
        setError('No se encontró una cámara. Por favor, conecte una cámara o use la opción de subir imagen.');
      } else {
        setError(errorMsg);
      }

      setScanning(false);
      handleScanError();
    }
  };

  const handleImageUpload = async (file: File) => {
    setScanning(true);
    setError(null);

    try {
      // Resize image if needed
      const resizedImage = await resizeImage(file);
      const imageUrl = URL.createObjectURL(resizedImage);

      const codeReader = new BrowserMultiFormatReader();
      const result = await codeReader.decodeFromImageUrl(imageUrl);

      URL.revokeObjectURL(imageUrl);

      const qrContent = result.getText();
      handleQRDetected(qrContent);
    } catch (err) {
      console.error('Image decode error:', err);
      setError('No se pudo detectar un código QR en la imagen. Intente con otra imagen o use la cámara.');
      setScanning(false);
      handleScanError();
    }
  };

  const resizeImage = (file: File): Promise<Blob> => {
    return new Promise((resolve) => {
      const img = new Image();
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      img.onload = () => {
        const maxDim = 1280;
        let { width, height } = img;

        if (width > height && width > maxDim) {
          height = (height / width) * maxDim;
          width = maxDim;
        } else if (height > maxDim) {
          width = (width / height) * maxDim;
          height = maxDim;
        }

        canvas.width = width;
        canvas.height = height;
        ctx?.drawImage(img, 0, 0, width, height);

        canvas.toBlob((blob) => resolve(blob || file), 'image/jpeg', 0.8);
      };

      img.src = URL.createObjectURL(file);
    });
  };

  /**
   * Congela el frame actual del video como JPEG. Es lo ÚNICO que sale del componente en
   * modo captura.
   */
  const grabFrame = (): Promise<Blob | null> =>
    new Promise((resolve) => {
      const video = videoRef.current;
      if (!video || !video.videoWidth) {
        resolve(null);
        return;
      }

      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      canvas.getContext('2d')?.drawImage(video, 0, 0);
      canvas.toBlob((blob) => resolve(blob), 'image/jpeg', 0.9);
    });

  const captureFrameAndFinish = async () => {
    const frame = await grabFrame();
    stopCamera();

    if (!frame) {
      setError('No se pudo tomar la foto. Intenta de nuevo.');
      return;
    }

    onFrameCaptured?.(frame);
  };

  const handleQRDetected = (qrContent: string) => {
    if (captureMode) {
      // El texto detectado se usa como DISPARADOR y se descarta aquí mismo. Nunca se
      // propaga: el servidor lo relee de los bytes que se acaban de congelar.
      void captureFrameAndFinish();
      return;
    }

    stopCamera();
    onQRDetected?.(qrContent);
  };

  const handleScanError = () => {
    const newCount = attemptCount + 1;
    setAttemptCount(newCount);

    if (newCount >= maxAttempts) {
      modal.warning({
        title: 'Problemas escaneando QR',
        content: 'No se pudo detectar el código QR después de varios intentos. Por favor, intente de nuevo o use una imagen más clara del código QR.',
        okText: 'Intentar de nuevo',
        onOk: () => {
          setAttemptCount(0);
        },
      });
    }
  };

  const renderMethodSelection = () => (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Alert
        message={texts.selectTitle}
        description={texts.selectDescription}
        type="info"
        showIcon
      />

      <Card
        hoverable
        onClick={() => setMethod('camera')}
        style={{ cursor: 'pointer' }}
      >
        <Space>
          <CameraOutlined style={{ fontSize: 32, color: '#1890ff' }} />
          <div>
            <Title level={5} style={{ margin: 0 }}>
              Escanear con cámara
            </Title>
            <Text type="secondary">Use la cámara de su dispositivo</Text>
          </div>
        </Space>
      </Card>

      <Card
        hoverable
        onClick={() => setMethod('upload')}
        style={{ cursor: 'pointer' }}
      >
        <Space>
          <UploadOutlined style={{ fontSize: 32, color: '#52c41a' }} />
          <div>
            <Title level={5} style={{ margin: 0 }}>
              Subir imagen del QR
            </Title>
            <Text type="secondary">Seleccione una imagen desde su dispositivo</Text>
          </div>
        </Space>
      </Card>
    </Space>
  );

  const renderCameraView = () => (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      {error && (
        <Alert
          message="Error"
          description={error}
          type="error"
          showIcon
          closable
          onClose={() => setError(null)}
        />
      )}

      {scanning ? (
        <>
          <Alert
            message="Escaneando..."
            description={texts.cameraHint}
            type="info"
            showIcon
            icon={<ScanOutlined spin />}
          />

          <div
            style={{
              position: 'relative',
              width: '100%',
              maxWidth: 500,
              margin: '0 auto',
              backgroundColor: '#000',
              borderRadius: 8,
              overflow: 'hidden',
            }}
          >
            <video
              ref={videoRef}
              style={{
                width: '100%',
                height: 'auto',
                display: 'block',
              }}
              autoPlay
              playsInline
            />
          </div>

          {captureMode && (
            <Button
              type="primary"
              size="large"
              icon={<CameraOutlined />}
              onClick={() => void captureFrameAndFinish()}
              block
            >
              Tomar la foto ahora
            </Button>
          )}

          <Button
            danger
            icon={<CloseOutlined />}
            onClick={stopCamera}
            block={isMobile}
          >
            Detener Cámara
          </Button>
        </>
      ) : (
        <Button
          type="primary"
          size="large"
          icon={<CameraOutlined />}
          onClick={startCameraScanning}
          block
        >
          Iniciar Cámara
        </Button>
      )}
    </Space>
  );

  const renderUploadView = () => (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      {error && (
        <Alert
          message="Error"
          description={error}
          type="error"
          showIcon
          closable
          onClose={() => setError(null)}
        />
      )}

      <Alert
        message={texts.uploadTitle}
        description={texts.uploadDescription}
        type="info"
        showIcon
      />

      {scanning ? (
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>
            <Text>Procesando imagen...</Text>
          </div>
        </div>
      ) : (
        <Upload.Dragger
          name="qrImage"
          fileList={fileList}
          onChange={({ fileList: newFileList }) => setFileList(newFileList)}
          beforeUpload={(file) => {
            // Validate file type
            const isImage = file.type.startsWith('image/');
            if (!isImage) {
              setError('Por favor seleccione un archivo de imagen válido (JPG, PNG, etc.)');
              return false;
            }

            // Validate file size (max 10MB)
            const isLt10M = file.size / 1024 / 1024 < 10;
            if (!isLt10M) {
              setError('La imagen debe ser menor a 10MB');
              return false;
            }

            handleImageUpload(file);
            return false; // Prevent auto upload
          }}
          accept="image/*"
          maxCount={1}
        >
          <p className="ant-upload-drag-icon">
            <UploadOutlined style={{ fontSize: 48, color: '#1890ff' }} />
          </p>
          <p className="ant-upload-text">
            Click o arrastre una imagen aquí
          </p>
          <p className="ant-upload-hint">
            Formatos soportados: JPG, PNG, WEBP
          </p>
        </Upload.Dragger>
      )}
    </Space>
  );

  const renderContent = () => {
    // En modo captura la única vía es la cámara: subir un archivo ya es una entrada
    // aparte del flujo de gastos, y ofrecerla aquí llevaría al usuario por un camino
    // que decodifica el QR en el navegador — justo lo contrario de lo que este modo hace.
    if (captureMode) {
      return renderCameraView();
    }

    switch (method) {
      case 'camera':
        return renderCameraView();
      case 'upload':
        return renderUploadView();
      default:
        return renderMethodSelection();
    }
  };

  return (
    <Modal
      title={
        method === 'select' || captureMode ? (
          <Space>
            <ScanOutlined />
            <span>{captureMode ? 'Tomar foto del comprobante' : 'Escanear Código QR'}</span>
          </Space>
        ) : (
          <Button
            type="link"
            icon={<CloseOutlined />}
            onClick={() => setMethod('select')}
            style={{ padding: 0 }}
          >
            Volver a métodos
          </Button>
        )
      }
      open={visible}
      onCancel={onClose}
      footer={[
        <Button key="close" onClick={onClose}>
          Cerrar
        </Button>,
      ]}
      width={isMobile ? '100%' : 600}
      destroyOnClose
    >
      {renderContent()}
    </Modal>
  );
};

export default QRScannerModal;
